"use client";

import { cn, Typography } from "@health/ui";
import { Drawer, DrawerContent, DrawerTitle } from "@health/ui/components/drawer";
import { useIsMobile } from "@health/ui/hooks/use-mobile";
import { AlertCircleIcon, LockIcon, MailIcon, ShieldCheckIcon, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTrack } from "../analytics/useTrack";
import { AuthButton } from "../auth/components/AuthButton";
import { AuthInput } from "../auth/components/AuthInput";
import { PasswordStrengthIndicator } from "../auth/components/PasswordStrengthIndicator";
import { useAuthForgotPassword } from "../auth/hooks/useAuthForgotPassword";
import { useAuthSignin } from "../auth/hooks/useAuthSignin";
import { useAuthSignup } from "../auth/hooks/useAuthSignup";

type Screen = "signin" | "signup" | "forgot-password" | "forgot-password-sent";

type Input = {
  context?: string;
  screen?: Screen;
  defaultValues?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
};

type AuthModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  screen: "signin" | "signup";
  input: Record<string, unknown>;
  output?: unknown;
  onSubmit: (result: unknown) => void;
  submitted?: boolean;
  /**
   * In `blocking` mode the modal cannot be dismissed via X / backdrop / Esc —
   * close affordances are hidden or no-op'd. Only a successful submit closes
   * it. Server-side enforcement (`processAuthWidgetOutputs`) is the real
   * guard; this prop is the cooperative-client UX.
   */
  authMode?: "blocking" | "dismissible";
};

export function AuthModal({
  open,
  onOpenChange,
  screen: initialScreen,
  input,
  output,
  onSubmit,
  submitted,
  authMode = "dismissible",
}: AuthModalProps) {
  const { context, defaultValues } = input as unknown as Input;
  const isMobile = useIsMobile();
  const isBlocking = authMode === "blocking";

  const [screen, setScreen] = useState<Screen>(initialScreen);
  const signin = useAuthSignin(defaultValues?.email);
  const signup = useAuthSignup(defaultValues);
  const forgotPassword = useAuthForgotPassword(signin.email);
  const track = useTrack();

  useEffect(() => {
    if (open) {
      setScreen(initialScreen);
    }
  }, [open, initialScreen]);

  const handleClose = () => {
    if (isBlocking) return;
    onOpenChange(false);
  };

  const renderSignin = () => (
    <div className="flex flex-col gap-6">
      {context && (
        <div className="space-y-2">
          <p className="text-sm text-(--color-text-primary)/70">{context}</p>
        </div>
      )}

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          track("authenticate:attempt", { auth_method: "signin" });
          const result = await signin.submit();
          if (result.success) {
            track("authenticate:success", { auth_method: "signin" });
            onSubmit({
              kind: "authenticated",
              method: "signin",
              email: signin.email.trim().toLowerCase(),
            });
          } else {
            track("authenticate:error", {
              auth_method: "signin",
              error_code: "invalid_credentials",
            });
          }
        }}
        className="flex flex-col gap-3.75"
      >
        <AuthInput
          variant="light"
          type="email"
          name="email"
          value={signin.email}
          onChange={(value) => {
            signin.setEmail(value);
            signin.setError("");
          }}
          placeholder="Email address"
          autoComplete="email"
          required
          disabled={signin.isLoading}
          error={signin.error && !signin.email.trim() ? "Required" : undefined}
          icon={<MailIcon size={20} />}
        />

        <AuthInput
          variant="light"
          type="password"
          name="password"
          value={signin.password}
          onChange={(value) => {
            signin.setPassword(value);
            signin.setError("");
          }}
          placeholder="Password"
          autoComplete="current-password"
          required
          disabled={signin.isLoading}
          error={signin.error && !signin.password ? "Required" : signin.error}
          icon={<ShieldCheckIcon size={20} />}
        />

        <div className="text-right">
          <button
            type="button"
            onClick={() => {
              forgotPassword.setEmail(signin.email);
              forgotPassword.reset();
              setScreen("forgot-password");
            }}
            className="text-[12px] text-(--color-accent) hover:opacity-80 transition-opacity cursor-pointer"
          >
            Forgot your password?
          </button>
        </div>

        <AuthButton
          theme="light"
          type="submit"
          variant="primary"
          loading={signin.isLoading}
          disabled={signin.isLoading || !signin.email.trim() || !signin.password}
          icon={<LockIcon size={16} />}
          className="mt-4"
        >
          Sign in securely
        </AuthButton>
      </form>

      <div className="text-center">
        <p className="text-[12px] leading-[24.85px] tracking-[-0.33px] text-(--color-text-primary)/50">
          Don't have an account yet?{" "}
          <button
            type="button"
            onClick={() => setScreen("signup")}
            className="cursor-pointer text-(--color-accent) hover:opacity-80 transition-opacity underline"
          >
            Create an account
          </button>
        </p>
      </div>

      <p className="text-[12px] leading-[16.3px] tracking-[-0.57px] text-(--color-text-primary)/80 text-center">
        By continuing, you agree to the Hims & Hers{" "}
        <span className="underline cursor-pointer">Terms of Service</span> and{" "}
        <span className="underline cursor-pointer">Privacy Policy</span>.
      </p>
    </div>
  );

  const renderSignup = () => (
    <div className="flex flex-col gap-6">
      {context && (
        <div className="space-y-2">
          <p className="text-sm text-(--color-text-primary)/70">{context}</p>
        </div>
      )}

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          track("authenticate:attempt", { auth_method: "signup" });
          const result = await signup.submit();
          if (result.success) {
            track("authenticate:success", { auth_method: "signup" });
            onSubmit({
              kind: "authenticated",
              method: "signup",
              email: signup.email.trim().toLowerCase(),
              firstName: signup.firstName.trim(),
              lastName: signup.lastName.trim(),
            });
          } else {
            track("authenticate:error", {
              auth_method: "signup",
              error_code: "invalid_credentials",
            });
          }
        }}
        className="flex flex-col gap-3.75"
      >
        {signup.showNameFields && (
          <div className="grid grid-cols-2 gap-3">
            <AuthInput
              variant="light"
              type="text"
              name="firstName"
              value={signup.firstName}
              onChange={(value) => {
                signup.setFirstName(value);
                signup.setError("");
              }}
              placeholder="First name"
              autoComplete="given-name"
              required
              disabled={signup.isLoading}
              error={signup.error && !signup.firstName.trim() ? "Required" : undefined}
            />

            <AuthInput
              variant="light"
              type="text"
              name="lastName"
              value={signup.lastName}
              onChange={(value) => {
                signup.setLastName(value);
                signup.setError("");
              }}
              placeholder="Last name"
              autoComplete="family-name"
              required
              disabled={signup.isLoading}
              error={signup.error && !signup.lastName.trim() ? "Required" : undefined}
            />
          </div>
        )}

        <AuthInput
          variant="light"
          type="email"
          name="email"
          value={signup.email}
          onChange={(value) => {
            signup.setEmail(value);
            signup.setError("");
          }}
          placeholder="Email address"
          autoComplete="email"
          required
          disabled={signup.isLoading}
          error={signup.error && !signup.email.trim() ? "Required" : undefined}
          icon={<MailIcon size={20} />}
        />

        <div className="space-y-2">
          <AuthInput
            variant="light"
            type="password"
            name="password"
            value={signup.password}
            onChange={(value) => {
              signup.setPassword(value);
              signup.setError("");
            }}
            placeholder="Password"
            autoComplete="new-password"
            required
            disabled={signup.isLoading}
            error={signup.error && !signup.password ? "Required" : undefined}
            icon={<ShieldCheckIcon size={20} />}
          />
          <PasswordStrengthIndicator password={signup.password} />
        </div>

        {signup.error && (
          <div
            className="flex items-center gap-2 text-sm mt-1 text-(--color-error-text)"
            role="alert"
          >
            <AlertCircleIcon size={12} />
            {signup.error}
          </div>
        )}

        <AuthButton
          theme="light"
          type="submit"
          variant="primary"
          loading={signup.isLoading}
          disabled={
            signup.isLoading ||
            !signup.firstName.trim() ||
            !signup.lastName.trim() ||
            !signup.email.trim() ||
            !signup.password
          }
          icon={<LockIcon size={16} />}
          className="mt-4"
        >
          Create an account
        </AuthButton>
      </form>

      <div className="text-center">
        <p className="text-[12px] leading-[24.85px] tracking-[-0.33px] text-(--color-text-primary)/50">
          Already have an account?{" "}
          <button
            type="button"
            onClick={() => setScreen("signin")}
            className="cursor-pointer text-(--color-accent) hover:opacity-80 transition-opacity underline"
          >
            Sign in
          </button>
        </p>
      </div>

      <p className="text-[12px] leading-[16.3px] tracking-[-0.57px] text-(--color-text-primary)/80 text-center">
        By continuing, you agree to the Hims & Hers{" "}
        <span className="underline cursor-pointer">Terms of Service</span> and{" "}
        <span className="underline cursor-pointer">Privacy Policy</span>.
      </p>
    </div>
  );

  const renderForgotPassword = () => (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-(--color-text-primary)/70">
        Enter your email and we'll send a link to reset your password
      </p>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const result = await forgotPassword.submit();
          if (result.success) {
            setScreen("forgot-password-sent");
          }
        }}
        className="flex flex-col gap-3.75"
      >
        <AuthInput
          variant="light"
          type="email"
          name="email"
          value={forgotPassword.email}
          onChange={(value) => {
            forgotPassword.setEmail(value);
            forgotPassword.setError("");
          }}
          placeholder="Email"
          autoComplete="email"
          required
          disabled={forgotPassword.isLoading}
          error={forgotPassword.error || undefined}
          icon={<MailIcon size={20} />}
        />

        <AuthButton
          theme="light"
          type="submit"
          variant="primary"
          loading={forgotPassword.isLoading}
          disabled={forgotPassword.isLoading || !forgotPassword.email.trim()}
          className="mt-4"
        >
          Reset password
        </AuthButton>
      </form>

      <div className="text-center">
        <p className="text-[12px] leading-[24.85px] tracking-[-0.33px] text-(--color-text-primary)/50">
          Remember your password?{" "}
          <button
            type="button"
            onClick={() => setScreen("signin")}
            className="cursor-pointer text-(--color-accent) hover:opacity-80 transition-opacity underline"
          >
            Back to login
          </button>
        </p>
      </div>
    </div>
  );

  const renderForgotPasswordSent = () => (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-(--color-text-primary)/70">
        You should receive an email shortly with a link to reset your password. Once you've reset
        it, come back here to log in.
      </p>

      <div className="text-left">
        <p className="text-[12px] leading-[24.85px] tracking-[-0.33px] text-(--color-text-primary)/50">
          Didn't receive it?{" "}
          <button
            type="button"
            onClick={() => forgotPassword.resend()}
            disabled={forgotPassword.isLoading}
            className="cursor-pointer text-(--color-accent) hover:opacity-80 transition-opacity underline disabled:opacity-50"
          >
            Resend link
          </button>
        </p>
      </div>

      <AuthButton
        theme="light"
        type="button"
        variant="primary"
        onClick={() => setScreen("signin")}
        className="mt-4"
      >
        Back to login
      </AuthButton>
    </div>
  );

  const getTitle = () => {
    switch (screen) {
      case "signin":
        return "Welcome back";
      case "signup":
        return "Create an account";
      case "forgot-password":
        return "Reset your password";
      case "forgot-password-sent":
        return "Check your email";
    }
  };

  const content = (
    <div className="h-full flex flex-col">
      <div className="shrink-0 flex items-center justify-between px-8 pt-6.5 pb-6">
        <Typography.Question className="leading-[1.18] tracking-[-0.9px] font-normal text-(--color-text-primary)">
          {getTitle()}
        </Typography.Question>
        {!isBlocking && (
          <button
            onClick={handleClose}
            className="w-10.5 h-10.5 flex items-center justify-center bg-(--color-text-primary)/5 hover:bg-(--color-text-primary)/10 rounded-[21px] transition-colors"
            aria-label="Close"
          >
            <X size={18} className="text-(--color-text-primary)/70" />
          </button>
        )}
      </div>

      <div className="flex-1 flex flex-col px-8 pb-12 overflow-y-auto">
        {screen === "signin" && renderSignin()}
        {screen === "signup" && renderSignup()}
        {screen === "forgot-password" && renderForgotPassword()}
        {screen === "forgot-password-sent" && renderForgotPasswordSent()}
      </div>
    </div>
  );

  const title = getTitle();

  return (
    <Drawer
      key={isMobile ? "mobile" : "desktop"}
      open={open}
      onOpenChange={(next) => {
        // Blocking mode: ignore close attempts from backdrop / Esc / swipe.
        // Only an explicit successful submit closes the modal (via
        // setModalOpen(false) in AuthWidget.handleModalSubmit).
        if (isBlocking && !next) return;
        onOpenChange(next);
      }}
      direction={isMobile ? "bottom" : "right"}
      modal={isMobile}
      pushWidth={isMobile ? undefined : "480px"}
      repositionInputs={false}
      dismissible={!isBlocking}
    >
      <DrawerContent className={cn("border-none", !isMobile && "p-4")}>
        <div className={cn("h-full", !isMobile && "shadow h-full rounded-2xl bg-accent-light/20")}>
          <DrawerTitle className="sr-only">{title}</DrawerTitle>
          {content}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
